import type { Service, Customer, ContactRequest, DashboardStats } from '../types/api.types';
import api from './api';

const KEYS = {
  CONTACTS: 'bhs_contacts',
};

// ── Admin credentials ─────────────────────────────────────────────────────────
export const ADMIN_EMAIL = 'admin@top-bhs.de';
export const ADMIN_PASSWORD = 'Admin1234!';

// ── Services (API-backed) ─────────────────────────────────────────────────────

export async function getServices(): Promise<Service[]> {
  const res = await api.get<Service[]>('/services');
  return res.data;
}

export async function getAdminServices(): Promise<Service[]> {
  const res = await api.get<Service[]>('/admin/services');
  return res.data;
}

export async function getServiceById(id: string | number): Promise<Service | null> {
  const services = await getServices();
  return services.find((s) => String(s.id) === String(id)) ?? null;
}

export async function saveService(data: Omit<Service, 'id' | 'order_index' | 'is_active'>): Promise<Service> {
  const res = await api.post<Service>('/admin/services', data);
  return res.data;
}

export async function updateService(id: string | number, data: Partial<Service>): Promise<Service> {
  const res = await api.put<Service>(`/admin/services/${id}`, data);
  return res.data;
}

export async function deleteService(id: string | number): Promise<void> {
  await api.delete(`/admin/services/${id}`);
}

// ── Customers (API-backed) ────────────────────────────────────────────────────

export async function getCustomers(): Promise<Customer[]> {
  const res = await api.get<Customer[]>('/customers');
  return res.data;
}

function toFormData(data: Record<string, unknown>, file?: File): FormData {
  const fd = new FormData();
  Object.entries(data).forEach(([k, v]) => { if (v != null) fd.append(k, String(v)); });
  if (file) fd.append('logo_file', file);
  return fd;
}

// Multipart config: remove Content-Type so browser sets multipart/form-data with boundary
const multipartHeaders = { headers: { 'Content-Type': false as unknown as string } };

export async function saveCustomer(data: Omit<Customer, 'id'>, logoFile?: File): Promise<Customer> {
  if (logoFile) {
    const res = await api.post<Customer>('/admin/customers', toFormData(data as Record<string, unknown>, logoFile), multipartHeaders);
    return res.data;
  }
  const res = await api.post<Customer>('/admin/customers', data);
  return res.data;
}

export async function updateCustomer(id: string | number, data: Partial<Customer>, logoFile?: File): Promise<Customer> {
  if (logoFile) {
    const fd = toFormData(data as Record<string, unknown>, logoFile);
    fd.append('_method', 'PUT');
    const res = await api.post<Customer>(`/admin/customers/${id}`, fd, multipartHeaders);
    return res.data;
  }
  const res = await api.put<Customer>(`/admin/customers/${id}`, data);
  return res.data;
}

export async function deleteCustomer(id: string | number): Promise<void> {
  await api.delete(`/admin/customers/${id}`);
}

// ── Contacts ──────────────────────────────────────────────────────────────────

export function getContacts(): ContactRequest[] {
  const raw = localStorage.getItem(KEYS.CONTACTS);
  return raw ? (JSON.parse(raw) as ContactRequest[]) : [];
}

export function saveContact(data: Omit<ContactRequest, 'id' | 'status' | 'created_at'>): void {
  const contacts = getContacts();
  const newContact: ContactRequest = {
    ...data,
    id: Date.now(),
    status: 'new',
    created_at: new Date().toISOString(),
  };
  localStorage.setItem(KEYS.CONTACTS, JSON.stringify([...contacts, newContact]));
}

export function updateContactStatus(id: string | number, status: ContactRequest['status']): void {
  const contacts = getContacts();
  const index = contacts.findIndex((c) => String(c.id) === String(id));
  if (index !== -1) {
    contacts[index] = { ...contacts[index], status };
    localStorage.setItem(KEYS.CONTACTS, JSON.stringify(contacts));
  }
}

export function deleteContact(id: string | number): void {
  const contacts = getContacts().filter((c) => String(c.id) !== String(id));
  localStorage.setItem(KEYS.CONTACTS, JSON.stringify(contacts));
}

// ── Lohnbuchhaltung-Preise ────────────────────────────────────────────────────

/** Returns a map of { labelKey: price } from the DB */
export async function getPrices(): Promise<Record<string, string>> {
  const res = await api.get<Record<string, string>>('/prices');
  return res.data;
}

/** Bulk-upserts all price items */
export async function updatePrices(items: { label_key: string; price: string }[]): Promise<void> {
  await api.put('/admin/prices', { items });
}

// ── Site Content ───────────────────────────────────────────────────────────────
export async function getSiteContent(key: string): Promise<Record<string, unknown>> {
  const res = await api.get<Record<string, unknown>>(`/content/${key}`);
  return res.data ?? {};
}

export async function updateSiteContent(key: string, content: Record<string, unknown>): Promise<void> {
  await api.put(`/admin/content/${key}`, { content });
}

// ── Dashboard Stats ───────────────────────────────────────────────────────────

export async function getDashboardStats(): Promise<DashboardStats> {
  const [services, contacts] = await Promise.all([
    getAdminServices().catch(() => [] as Service[]),
    Promise.resolve(getContacts()),
  ]);
  return {
    services: services.filter((s) => s.is_active).length,
    kooperation: 0,
    totalContacts: contacts.length,
    newContacts: contacts.filter((c) => c.status === 'new').length,
  };
}
